import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { getResumenTrabajo } from '../api/client'
import PageHeader from '../components/PageHeader'
import TableroTrabajoOt from '../components/ot/TableroTrabajoOt'
import Periodo from '../components/ot/Periodo'
import { rangoDe } from '../components/ot/rangos'
import { formatFecha } from '../components/unidades/tipos'

// Operaciones > Órdenes de trabajo > Asignaciones, like Mantenimiento's: the
// board to put people to work on the OTs in execution (the default view), and
// "Trabajo por persona" -- who worked on which OTs in a period, and how much,
// OT work and parte capsules alike (GET /api/ot/trabajo/resumen). The view
// lives in the address (?vista=).
const VISTAS = ['tablero', 'resumen']

const SERIE = '#2a78d6'                      // one series: hours (validated, light surface)
const horas = (s) => Math.round((s / 3600) * 10) / 10
const fmt = (s) => (s > 0 && s < 3600 ? `${Math.max(1, Math.round(s / 60))} min` : `${horas(s).toLocaleString('es-CL', { maximumFractionDigits: 1 })} h`)

export default function PlantaTrabajo() {
  const { t } = useTranslation('operations')
  const [sp, setSp] = useSearchParams()
  const vista = VISTAS.includes(sp.get('vista')) ? sp.get('vista') : 'tablero'
  return (
    <div className="p-8">
      <div className="mb-6"><PageHeader title={t('ot.asignar.titulo')} subtitle={t('ot.asignar.subtitulo')} /></div>
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 mb-5">
        {VISTAS.map(v => (
          <button key={v} type="button" onClick={() => setSp(v === 'tablero' ? {} : { vista: v }, { replace: true })}
            className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
              vista === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            {t(`ot.asignar.vistas.${v}`)}
          </button>
        ))}
      </div>
      {vista === 'tablero' ? <TableroTrabajoOt /> : <ResumenTrabajo />}
    </div>
  )
}

// Who worked on which OTs in a period: pick a person to see their OTs and
// days; an OT opens its panel.
function ResumenTrabajo() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [rango, setRango] = useState(() => rangoDe('30'))
  const [area, setArea] = useState('')
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState('')
  const [elegida, setElegida] = useState(null)
  const [buscar, setBuscar] = useState('')

  useEffect(() => {
    let vivo = true
    getResumenTrabajo({ ...rango, ...(area ? { area } : {}) })
      .then(d => { if (vivo) { setDatos(d); setError('') } })
      .catch(err => { if (vivo) setError(err.response?.status === 403 ? t('ot.trabajo.sinFuncion') : t('ot.trabajo.cargaFallo')) })
    return () => { vivo = false }
  }, [rango, area, t])

  const personas = useMemo(() => (datos || []).filter(p => !buscar.trim() || p.nombre.toLowerCase().includes(buscar.trim().toLowerCase())), [datos, buscar])
  const total = (datos || []).reduce((s, p) => s + p.segundos, 0)
  const extra = (datos || []).reduce((s, p) => s + p.segundos_extra, 0)
  const conTrabajo = (datos || []).filter(p => p.segundos > 0).length
  const ots = new Set((datos || []).flatMap(p => p.ots.map(o => o.clave))).size
  const ahora = (datos || []).filter(p => p.trabajando).length
  const max = Math.max(1, ...personas.map(p => p.segundos))
  const sel = (datos || []).find(p => p.persona_id === elegida) || null
  const areas = useMemo(() => [...new Set((datos || []).flatMap(p => p.areas))].sort(), [datos])

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Periodo valor={rango} onChange={setRango} />
        <select value={area} onChange={e => setArea(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('ot.trabajo.todasAreas')}</option>
          <option value="planta">{t('ot.trabajo.areaPlanta')}</option>
          <option value="mantenimiento">{t('ot.trabajo.areaMantenimiento')}</option>
        </select>
        <span className="text-xs text-gray-400">{formatFecha(rango.desde)} – {formatFecha(rango.hasta)}</span>
      </div>
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Tile etiqueta={t('ot.trabajo.kpi.horas')} valor={datos ? fmt(total) : '…'} />
        <Tile etiqueta={t('ot.trabajo.kpi.personas')} valor={datos ? `${conTrabajo} / ${datos.length}` : '…'} nota={t('ot.trabajo.kpi.personasNota')} />
        <Tile etiqueta={t('ot.trabajo.kpi.ots')} valor={datos ? ots : '…'} />
        <Tile etiqueta={t('ot.trabajo.kpi.extra')} valor={datos ? fmt(extra) : '…'} nota={total ? t('ot.trabajo.kpi.extraNota', { pct: Math.round((100 * extra) / total) }) : null} />
        <Tile etiqueta={t('ot.trabajo.kpi.ahora')} valor={datos ? ahora : '…'} />
      </div>

      <div className="grid lg:grid-cols-[minmax(320px,2fr)_3fr] gap-5 items-start">
        <section className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">{t('ot.trabajo.porPersona')}</h3>
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('ot.trabajo.buscar')}
              className="border border-gray-200 rounded-md px-2 py-1 text-xs w-40" />
          </div>
          {!datos ? <p className="px-4 py-6 text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p>
            : !personas.length ? <p className="px-4 py-6 text-sm text-gray-400">{t('ot.trabajo.sinPersonas')}</p> : (
              <ul className="divide-y divide-gray-50 max-h-[560px] overflow-y-auto">
                {personas.map(p => (
                  <li key={p.persona_id}>
                    <button type="button" onClick={() => setElegida(id => (id === p.persona_id ? null : p.persona_id))}
                      title={`${p.nombre}: ${fmt(p.segundos)} · ${t('ot.trabajo.nOts', { count: p.ots.length })}`}
                      className={`w-full px-4 py-2.5 text-left transition-colors ${elegida === p.persona_id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate font-medium text-gray-900">{p.nombre}</span>
                        <span className="shrink-0 tabular-nums text-gray-700">{fmt(p.segundos)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
                          <span className="block h-full rounded-full" style={{ width: `${(100 * p.segundos) / max}%`, background: SERIE }} />
                        </span>
                        <span className="w-24 shrink-0 text-right text-[11px] text-gray-500">{t('ot.trabajo.nOts', { count: p.ots.length })}</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-500">
                        <span>{[p.cargo, p.areas.join(', ')].filter(Boolean).join(' · ')}</span>
                        {p.trabajando && <span className="text-emerald-700 font-medium">● {t('ot.trabajo.enAhora', { ot: p.trabajando })}</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 min-h-[300px]">
          {!sel ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-sm text-gray-400 py-16">
              <p>{t('ot.trabajo.elige')}</p>
              {areas.length === 0 && datos && <p className="text-xs mt-1">{t('ot.trabajo.eligeNota')}</p>}
            </div>
          ) : (
            <Detalle p={sel} t={t} onAbrirOt={id => navigate(`/operations/planta/ot?ot=${id}&vista=lista`)} />
          )}
        </section>
      </div>
    </div>
  )
}

function Tile({ etiqueta, valor, nota }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{etiqueta}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{valor}</p>
      {nota && <p className="text-[11px] text-gray-500">{nota}</p>}
    </div>
  )
}

function Detalle({ p, t, onAbrirOt }) {
  const dias = p.por_dia.map(d => ({ dia: d.dia, etiqueta: formatFecha(d.dia).slice(0, 5), horas: horas(d.segundos) }))
  const maxOt = Math.max(1, ...p.ots.map(o => o.segundos))
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{p.nombre}</h3>
        <p className="text-xs text-gray-500">{[p.cargo, p.areas.join(', ')].filter(Boolean).join(' · ')}</p>
        <p className="mt-1 text-sm text-gray-700">
          {t('ot.trabajo.resumenPersona', { horas: fmt(p.segundos), count: p.ots.length })}
          {p.segundos_extra > 0 && <span className="text-gray-500"> · {t('ot.trabajo.deExtra', { horas: fmt(p.segundos_extra) })}</span>}
        </p>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.trabajo.enQueOts')}</h4>
        {!p.ots.length ? <p className="text-sm text-gray-400">{t('ot.trabajo.sinTrabajo')}</p> : (
          <table className="w-full text-sm">
            <tbody>
              {p.ots.map(o => (
                <tr key={o.clave} className="border-b border-gray-50 last:border-0">
                  <td className="py-1.5 pr-2 w-28 whitespace-nowrap">
                    {o.ot_id
                      ? <button type="button" onClick={() => onAbrirOt(o.ot_id)} className="font-mono text-xs font-semibold text-indigo-700 hover:underline">{o.codigo}</button>
                      : <span className="font-mono text-xs text-gray-600">{o.codigo}</span>}
                  </td>
                  <td className="py-1.5 pr-2 max-w-[220px] truncate text-gray-700" title={o.titulo || ''}>{o.titulo || '—'}</td>
                  <td className="py-1.5 pr-2 w-[34%]">
                    <span className="block h-2 rounded-full bg-gray-100 overflow-hidden" title={`${o.codigo}: ${fmt(o.segundos)}`}>
                      <span className="block h-full rounded-full" style={{ width: `${(100 * o.segundos) / maxOt}%`, background: SERIE }} />
                    </span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-900 w-20 whitespace-nowrap">{fmt(o.segundos)}</td>
                  <td className="py-1.5 pl-2 text-right text-[11px] text-gray-500 whitespace-nowrap">
                    {formatFecha(o.desde.slice(0, 10))}{o.desde.slice(0, 10) !== o.hasta.slice(0, 10) ? ` – ${formatFecha(o.hasta.slice(0, 10))}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dias.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.trabajo.porDia')}</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dias} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eceae4" />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#d6d3cc' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} allowDecimals />
                <Tooltip cursor={{ fill: '#f3f4f6' }} formatter={(v) => [`${v} h`, t('ot.trabajo.horas')]}
                  labelFormatter={(_, pl) => (pl?.[0] ? formatFecha(pl[0].payload.dia) : '')} />
                <Bar dataKey="horas" fill={SERIE} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}
